import classNames from 'classnames';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';

import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { AlmaLink } from '../../../alma-link';
import { PortableText } from '../../../portableText';
import * as styles from './full-bleed.module.css';

export function FullBleedHero({
	_rawHeroCopy,
	bottomPaddingOverride,
	topPaddingOverride,
	ctas = [],
	description,
	fadeEffects,
	fullBleedSpacing,
	header,
	horizontalAlignment,
	image,
	mobileImage,
	bgImageAlignment,
	svg,
	verticalAlignment,
	verticalPadding,
}) {
	// hooks
	const isMobile = useMediaQuery('(max-width: 799px)');
	const isTablet = useMediaQuery(
		'(min-width: 800px) and (max-width: 1279px)'
	);
	const isDesktop = useMediaQuery('(min-width: 1280px)');

	// state
	const [isMobileView, setIsMobileView] = useState(false);

	const fitImage85vh = fullBleedSpacing === 'fitImage85vh';
	const fitContent = fullBleedSpacing === 'fitContent';
	// content padding
	const modulePaddingTop = verticalPadding?.modulePaddingTop;
	const modulePaddingBottom = verticalPadding?.modulePaddingBottom;

	// TODO: refactor this into a hook - HOOK ALREADY EXISTS - SWAPPY SWAPPY
	const setViewHandler = () => {
		setIsMobileView(matchMedia('(max-width:767px)').matches);
	};

	useEffect(() => {
		if (
			typeof window !== undefined &&
			window.matchMedia('(max-width:767px)')
		) {
			setViewHandler();
		}
		window.addEventListener('resize', setViewHandler);
		return () => {
			window.removeEventListener('resize', setViewHandler);
		};
	}, []);

	const paddingOverrideEnabled =
		bottomPaddingOverride?.bottomPaddingOverrideEnabled;

	const bottomPaddingOverrideStyle = {
		paddingBottom: isMobile
			? `${bottomPaddingOverride?.bottomPaddingOverrideMobile}px`
			: isTablet
				? `${bottomPaddingOverride?.bottomPaddingOverrideTablet}px`
				: isDesktop
					? `${bottomPaddingOverride?.bottomPaddingOverrideDesktop}px`
					: null,
	};

	const topPaddingOverrideEnabled =
		topPaddingOverride?.topPaddingOverrideEnabled;

	const topPaddingOverrideStyle = {
		paddingTop: isMobile
			? `${topPaddingOverride?.topPaddingOverrideMobile}px`
			: isTablet
				? `${topPaddingOverride?.topPaddingOverrideTablet}px`
				: isDesktop
					? `${topPaddingOverride?.topPaddingOverrideDesktop}px`
					: null,
	};

	// image data
	const desktopImageData = getImage(image?.asset);
	const mobileImageData = getImage(mobileImage?.asset);

	// preview image data
	const desktopPreviewImage = image?.asset?.url;
	const mobilePreviewImage = mobileImage?.asset?.url;

	// fade effects
	const enableFadeEffect = fadeEffects?.enableFadeEffect;
	const fadeEffectDirection = fadeEffects?.fadeEffectDirection;

	return (
		<div
			className={classNames(
				styles.moduleContainer,
				fitImage85vh && styles.module85vh,
				fitContent && styles.moduleFitContent
			)}
		>
			<div
				className={classNames(
					styles.imageContainer,
					bgImageAlignment && styles[bgImageAlignment]
				)}
			>
				{isMobileView && mobileImageData ? (
					<GatsbyImage
						alt={mobileImage?.alt || ''}
						className={styles.mobileImage}
						image={mobileImageData}
					/>
				) : desktopImageData ? (
					<GatsbyImage
						alt={image?.alt || ''}
						className={styles.image}
						image={desktopImageData}
					/>
				) : null}
				{/* render separate image components for preview images */}
				{!mobilePreviewImage && isMobileView && mobilePreviewImage ? (
					<img
						alt={mobileImage?.alt || ''}
						className={styles.mobileImage}
						src={mobilePreviewImage}
					/>
				) : !desktopImageData && desktopPreviewImage ? (
					<img
						alt={image?.alt || ''}
						className={styles.image}
						src={desktopPreviewImage}
					/>
				) : null}
			</div>
			<div
				className={classNames(
					enableFadeEffect && 'reveal',
					fadeEffectDirection && fadeEffectDirection,
					styles.contentContainer,
					styles[verticalAlignment],
					styles[horizontalAlignment],
					modulePaddingTop,
					modulePaddingBottom
				)}
				style={{
					...(paddingOverrideEnabled
						? bottomPaddingOverrideStyle
						: {}),
					...(topPaddingOverrideEnabled
						? topPaddingOverrideStyle
						: {}),
				}}
			>
				{svg && <div dangerouslySetInnerHTML={{ __html: svg }} />}
				{_rawHeroCopy && <PortableText blocks={_rawHeroCopy} />}
				{/* header is only used for landing page templates */}
				{header && <h1 className='heading-xl'>{header}</h1>}
				{/* description is only used for landing page templates */}
				{description && <p className='body-xl-bold'>{description}</p>}
				{ctas && (
					<div className={styles.ctaContainer}>
						{ctas.map((cta, key) => {
							return <AlmaLink cta={cta} key={key} />;
						})}
					</div>
				)}
			</div>
		</div>
	);
}
